<template>
  <div class="recommended-angle-container">
    <div class="angle-header">
      <h1 class="angle-title">Recommended Photo Angle</h1>
      <h2 class="angle-subtitle">{{ recommended_instruction || 'Full Body Cut' }}</h2>
    </div>
    
    <div class="angle-examples">
      <div class="example-card recommended">
        <div class="example-image">
          <img 
            :src="currentRecommendedImage" 
            alt="Recommended angle"
            class="angle-image"
          />
        </div>
        <div class="status-indicator recommended">
          <div class="status-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17L4 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
      
      <div class="navigation-hint">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="#ccc" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      
      <div class="example-card not-recommended">
        <div class="example-image">
          <img 
            :src="currentNotRecommendedImage" 
            alt="Not recommended angle"
            class="angle-image"
          />
        </div>
        <div class="status-indicator not-recommended">
          <div class="status-circle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
    
    <div class="navigation-container">
      <button class="nav-arrow left" @click="previousImage">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <div class="angle-info">{{ recommended_instruction || 'Use full-body photos for best results' }}</div>
      
      <button class="nav-arrow right" @click="nextImage">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
    
    <div v-if="hasMultipleImages" class="image-counter">
      {{ currentIndex + 1 }} / {{ totalImages }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'RecommendedPhotoAngle',
  props: {
    recommended_angle: {
      type: Array,
      default: () => []
    },
    not_recommended_angle: {
      type: Array,
      default: () => []
    },
    recommended_instruction: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentIndex: 0
    };
  },
  computed: {
    totalImages() {
      return Math.max(this.recommended_angle.length, this.not_recommended_angle.length);
    },
    hasMultipleImages() {
      return this.totalImages > 1;
    },
    currentRecommendedImage() {
      if (this.recommended_angle.length === 0) {
        return '/assets/images/examples/recommended-angle.jpg';
      }
      return this.recommended_angle[this.currentIndex] || this.recommended_angle[0];
    },
    currentNotRecommendedImage() {
      if (this.not_recommended_angle.length === 0) {
        return '/assets/images/examples/not-recommended-angle.jpg';
      }
      return this.not_recommended_angle[this.currentIndex] || this.not_recommended_angle[0];
    }
  },
  methods: {
    proceed() {
      this.$emit('proceed');
    },
    goBack() {
      this.$emit('skip');
    },
    nextImage() {
      if (this.currentIndex < this.totalImages - 1) {
        this.currentIndex++;
      } else {
        // If on last image, proceed to next step
        this.proceed();
      }
    },
    previousImage() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
      } else {
        // If on first image, go back
        this.goBack();
      }
    }
  }
};
</script>

<style scoped>
.recommended-angle-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.angle-header {
  text-align: center;
  margin-bottom: 3vw;
}

.angle-title {
  font-size: 2.5vw;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5vw;
}

.angle-subtitle {
  font-size: 1.5vw;
  color: #666;
  font-weight: 400;
}

.angle-examples {
  display: flex;
  align-items: center;
  gap: 2vw;
  margin-bottom: 3vw;
}

.example-card {
  position: relative;
  width: 12vw;
  height: 15vw;
  border-radius: 1vw;
  overflow: hidden;
  box-shadow: 0 0.2vw 1vw rgba(0,0,0,0.1);
}

.example-image {
  width: 100%;
  height: 100%;
}

.angle-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.status-indicator {
  position: absolute;
  bottom: 1vw;
  right: 1vw;
}

.status-circle {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-indicator.recommended .status-circle {
  background: #4CAF50;
}

.status-indicator.not-recommended .status-circle {
  background: #f44336;
}

.navigation-hint {
  color: #ccc;
  display: flex;
  align-items: center;
}

.navigation-hint svg {
  width: 2vw;
  height: 2vw;
}

.navigation-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 2vw;
}

.nav-arrow {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-arrow:hover {
  background: #f5f5f5;
  transform: scale(1.1);
}

.nav-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.angle-info {
  font-size: 1.1vw;
  color: #666;
  text-align: center;
}

.image-counter {
  margin-top: 1vw;
  font-size: 1vw;
  color: #999;
  text-align: center;
}

/* Mobile styles */
@media (max-width: 768px) {
  .recommended-angle-container {
    padding: 0;
  }

  .angle-title {
    font-size: 6vw;
    margin-bottom: 2vw;
  }

  .angle-subtitle {
    font-size: 4vw;
  }

  .angle-examples {
    gap: 4vw;
    margin-bottom: 6vw;
  }

  .example-card {
    width: 30vw;
    height: 37vw;
    border-radius: 2vw;
  }

  .status-indicator {
    bottom: 2vw;
    right: 2vw;
  }

  .status-circle {
    width: 8vw;
    height: 8vw;
  }

  .status-circle svg {
    width: 4vw;
    height: 4vw;
  }

  .navigation-hint svg {
    width: 5vw;
    height: 5vw;
  }

  .navigation-container {
    padding: 0 4vw;
  }

  .nav-arrow {
    width: 8vw;
    height: 8vw;
    border: 0.3vw solid #333;
  }

  .nav-arrow svg {
    width: 4vw;
    height: 4vw;
  }

  .angle-info {
    font-size: 3vw;
  }
  
  .image-counter {
    margin-top: 2vw;
    font-size: 2.5vw;
  }
}
</style> 